import { useIntl, type IntlShape } from 'react-intl';
import { PageFilterType, SeoLinksSectionKey } from '@/types/locationDetailsPages/shared';

type HeadingKey = SeoLinksSectionKey | 'CITY_CITIES' | 'CITY_PARKS' | 'CITY_TRAILS';

type SeoLinksTrailSectionHeaderWithFilterProps = {
  filter?: PageFilterType;
  intl: IntlShape;
};

const getTrailSectionHeaderWithFilter = ({ filter, intl }: SeoLinksTrailSectionHeaderWithFilterProps): string => {
  const messageKey = filter || 'default';

  const translations = {
    default: intl.formatMessage({ defaultMessage: 'More trails' }),
    backpacking: intl.formatMessage({ defaultMessage: 'More backpacking trails' }),
    'bike-touring': intl.formatMessage({ defaultMessage: 'More bike touring trails' }),
    birding: intl.formatMessage({ defaultMessage: 'More birding trails' }),
    camping: intl.formatMessage({ defaultMessage: 'More camping trails' }),
    'cross-country-skiing': intl.formatMessage({ defaultMessage: 'More cross-country skiing trails' }),
    fishing: intl.formatMessage({ defaultMessage: 'More fishing trails' }),
    'horseback-riding': intl.formatMessage({ defaultMessage: 'More horseback riding trails' }),
    'mountain-biking': intl.formatMessage({ defaultMessage: 'More mountain biking trails' }),
    'off-road-driving': intl.formatMessage({ defaultMessage: 'More off-road driving trails' }),
    'paddle-sports': intl.formatMessage({ defaultMessage: 'More paddling trails' }),
    'road-biking': intl.formatMessage({ defaultMessage: 'More road biking trails' }),
    'rock-climbing': intl.formatMessage({ defaultMessage: 'More rock climbing trails' }),
    'scenic-driving': intl.formatMessage({ defaultMessage: 'More scenic driving trails' }),
    skiing: intl.formatMessage({ defaultMessage: 'More skiing trails' }),
    snowshoeing: intl.formatMessage({ defaultMessage: 'More snowshoeing trails' }),
    'trail-running': intl.formatMessage({ defaultMessage: 'More running trails' }),
    'via-ferrata': intl.formatMessage({ defaultMessage: 'More via ferrata trails' }),
    walking: intl.formatMessage({ defaultMessage: 'More walking trails' }),
    ada: intl.formatMessage({ defaultMessage: 'More wheelchair-friendly trails' }),
    beach: intl.formatMessage({ defaultMessage: 'More beach trails' }),
    cave: intl.formatMessage({ defaultMessage: 'More cave trails' }),
    'city-walk': intl.formatMessage({ defaultMessage: 'More city walks' }),
    'dogs-no': intl.formatMessage({ defaultMessage: 'More trails with no dogs' }),
    dogs: intl.formatMessage({ defaultMessage: 'More dog-friendly trails' }),
    'dogs-leash': intl.formatMessage({ defaultMessage: 'More trails for leashed dogs' }),
    forest: intl.formatMessage({ defaultMessage: 'More forest trails' }),
    'historic-site': intl.formatMessage({ defaultMessage: 'More trails with historic sites' }),
    'hot-springs': intl.formatMessage({ defaultMessage: 'More hot springs trails' }),
    kids: intl.formatMessage({ defaultMessage: 'More kid-friendly trails' }),
    lake: intl.formatMessage({ defaultMessage: 'More lake trails' }),
    'partially-paved': intl.formatMessage({ defaultMessage: 'More partially paved trails' }),
    paved: intl.formatMessage({ defaultMessage: 'More paved trails' }),
    'pub-crawl': intl.formatMessage({ defaultMessage: 'More pub walks' }),
    'rails-trails': intl.formatMessage({ defaultMessage: 'More rail trails' }),
    river: intl.formatMessage({ defaultMessage: 'More river trails' }),
    strollers: intl.formatMessage({ defaultMessage: 'More stroller-friendly trails' }),
    views: intl.formatMessage({ defaultMessage: 'More trails with views' }),
    waterfall: intl.formatMessage({ defaultMessage: 'More waterfall trails' }),
    'wild-flowers': intl.formatMessage({ defaultMessage: 'More wildflower trails' }),
    wildlife: intl.formatMessage({ defaultMessage: 'More wildlife trails' }),
    event: intl.formatMessage({ defaultMessage: 'More event trails' }),
    easy: intl.formatMessage({ defaultMessage: 'More easy trails' }),
    moderate: intl.formatMessage({ defaultMessage: 'More moderate trails' }),
    hard: intl.formatMessage({ defaultMessage: 'More hard trails' }),
    short: intl.formatMessage({ defaultMessage: 'More short trails' }),
    long: intl.formatMessage({ defaultMessage: 'More long trails' })
  };
  return translations[messageKey];
};

const getNonFilterSectionHeading = (intl: IntlShape, sectionKey: HeadingKey, linksCount: number) => {
  return {
    TRAILS: intl.formatMessage({ defaultMessage: 'More trails' }),
    PARKS: intl.formatMessage({ defaultMessage: 'Top {count} parks' }, { count: linksCount }),
    CITIES: intl.formatMessage({ defaultMessage: 'Top {count} cities' }, { count: linksCount }),
    REGIONS: intl.formatMessage({ defaultMessage: 'Top {count} regions' }, { count: linksCount }),
    STATES: intl.formatMessage({ defaultMessage: 'Top {count} states' }, { count: linksCount }),
    CITY_CITIES: intl.formatMessage({ defaultMessage: 'Top cities' }),
    CITY_PARKS: intl.formatMessage({ defaultMessage: 'Top parks' }),
    CITY_TRAILS: intl.formatMessage({ defaultMessage: 'More trails' })
  }[sectionKey];
};

export const useSectionHeading = (isCityPage: boolean, pageFilterUid?: PageFilterType) => {
  const intl = useIntl();

  const getSectionHeading = (sectionKey: SeoLinksSectionKey, linksCount: number) => {
    if (sectionKey === 'TRAILS' && pageFilterUid) {
      return getTrailSectionHeaderWithFilter({ filter: pageFilterUid, intl });
    }

    const headingKey = isCityPage ? `CITY_${sectionKey}` : sectionKey;
    return getNonFilterSectionHeading(intl, headingKey as HeadingKey, linksCount);
  };

  return { getSectionHeading };
};
